import { prefersReducedMotion } from '@theme/utilities';

const PILL_STICKY_SELECTOR = '.shopify-section.section-pill-list:has(.pill-list-section--sticky)';
const PILL_LINK_SELECTOR = 'a.pill-list__pill[href^="#"]';

/**
 * @param {string} value
 * @returns {number}
 */
function parseCssPx(value) {
  const n = parseFloat(String(value).trim());
  return Number.isFinite(n) ? n : 0;
}

/**
 * Mirrors `snippets/section.liquid` scroll-margin for `.section[id]`, plus sticky pill-list height.
 * @returns {number}
 */
function getAnchorScrollMarginPx() {
  const html = document.documentElement;
  const body = document.body;

  const base = parseCssPx(getComputedStyle(html).getPropertyValue('--scroll-margin')) || 50;

  let headerPx = parseCssPx(getComputedStyle(body).getPropertyValue('--header-group-height'));
  if (!headerPx) headerPx = parseCssPx(getComputedStyle(body).getPropertyValue('--header-height'));
  if (!headerPx) {
    const hg = document.getElementById('header-group');
    if (hg) headerPx = hg.offsetHeight;
  }

  let paddingSm = parseCssPx(getComputedStyle(html).getPropertyValue('--padding-sm'));
  if (!paddingSm) {
    const fs = parseFloat(getComputedStyle(html).fontSize) || 16;
    paddingSm = 0.7 * fs;
  }

  const pillSection = document.querySelector(PILL_STICKY_SELECTOR);
  const pillPx = pillSection ? pillSection.offsetHeight : 0;

  return Math.max(base, headerPx + paddingSm + pillPx);
}

function updatePillListAnchorOffsetVar() {
  const pillSection = document.querySelector(PILL_STICKY_SELECTOR);
  const px = pillSection ? pillSection.offsetHeight : 0;
  document.documentElement.style.setProperty('--pill-list-anchor-offset', `${px}px`);
}

/**
 * @param {string} id
 * @param {{ behavior?: ScrollBehavior }} [options]
 */
function scrollToHashTarget(id, options = {}) {
  const target = document.getElementById(id);
  if (!target) return false;

  const margin = getAnchorScrollMarginPx();
  const top = target.getBoundingClientRect().top + window.scrollY - margin;

  window.scrollTo({
    top: Math.max(0, top),
    behavior: options.behavior ?? (prefersReducedMotion() ? 'instant' : 'smooth'),
  });
  return true;
}

/** @param {MouseEvent} event */
function onPillLinkClick(event) {
  const a = event.target && /** @type {HTMLElement} */ (event.target).closest(PILL_LINK_SELECTOR);
  if (!a) return;

  const href = a.getAttribute('href');
  if (!href || href === '#' || href.length < 2) return;

  let id = href.slice(1);
  try {
    id = decodeURIComponent(id);
  } catch {
    /* use raw */
  }

  const target = document.getElementById(id);
  if (!target) return;

  event.preventDefault();
  scrollToHashTarget(id, {});
  history.pushState(null, '', href);
}

function adjustScrollForInitialHash() {
  const { hash } = window.location;
  if (!hash || hash.length < 2) return;

  let id = hash.slice(1);
  try {
    id = decodeURIComponent(id);
  } catch {
    /* use raw */
  }

  if (!document.getElementById(id)) return;

  requestAnimationFrame(() => {
    scrollToHashTarget(id, { behavior: 'instant' });
  });
}

let resizeTimer;

function onResize() {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => updatePillListAnchorOffsetVar(), 100);
}

function init() {
  updatePillListAnchorOffsetVar();

  document.addEventListener('click', onPillLinkClick);

  const pillSticky = document.querySelector(PILL_STICKY_SELECTOR);
  if (pillSticky && typeof ResizeObserver !== 'undefined') {
    const ro = new ResizeObserver(() => updatePillListAnchorOffsetVar());
    ro.observe(pillSticky);
  }

  window.addEventListener('resize', onResize);

  document.addEventListener('shopify:section:load', () => {
    updatePillListAnchorOffsetVar();
  });

  adjustScrollForInitialHash();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
